import {Rule, RuleObject} from 'antd/es/form';
import {TFunction} from 'i18next';
import {REGEX} from "@/constant/constants.ts";

export const AppValidator = (t: TFunction) => ({
    required: (fieldName: string, message = t('validation.required', {fieldName})): Rule => ({
        required: true,
        message,
    }),
    minLength: (length: number, fieldName = '', message = t('validation.maxString', {fieldName, length})): Rule => ({
        min: length,
        message,
    }),
    maxLength: (length: number, fieldName = '', message = t('validation.minString', {fieldName, length})): Rule => ({
        max: length,
        message,
    }),
    personalEmail: (message = t('validation.email')): Rule => ({
        validator(_: RuleObject, value: any): Promise<void> {
            const regex: RegExp = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?<!gmail\.com|yahoo\.com|hotmail\.com|163\.com|126\.com|sohu\.com|sina\.com|qq\.com|sougou.com|tom\.com|foxmail\.com|yeah\.net|icloud\.com|docomo\.ne\.ja|softbank\.ne\.jp|ezweb\.ne\.jp|rakuten\.co\.jp)$/;
            if (value && !regex.test(value)) {
                return Promise.reject(new Error(message));
            }
            return Promise.resolve();
        },
    }),
    custom: (validatorFn: () => void): Rule => ({
        validator: validatorFn,
    }),
    validateFormatWithRegex: (regex: RegExp, fieldName: string, message: string = t('validation.formatInvalid', {fieldName})) => ({
        validator(_: RuleObject, value: any): Promise<void> {
            if (value && !regex.test(value)) {
                return Promise.reject(new Error(message));
            }
            return Promise.resolve();
        },
    }),
    validateFileRequired: (fileList: any[], fieldName: string) => ({
        validator(): Promise<void> {
            if (fileList.length > 0) {
                return Promise.resolve();
            }
            const message = t('validation.required', {fieldName});
            return Promise.reject(new Error(message));
        },
    }),
    confirmPasswordMatch: (fieldToMatch: string, errorMessage: string, getFieldValue: any) => ({
        validator(_: RuleObject, value: any): Promise<void> {
            if (!value || getFieldValue(fieldToMatch) === value) {
                return Promise.resolve();
            }
            return Promise.reject(new Error(errorMessage));
        },
    }),
    confirmNewPassNotIncludeOldPass: (fieldToMatch: string, errorMessage: string, getFieldValue: any) => ({
        validator(_: RuleObject, value: any): Promise<void> {
            if (!value || !String(value).includes(getFieldValue(fieldToMatch))) {
                return Promise.resolve();
            }
            return Promise.reject(new Error(errorMessage));
        },
    }),
    validateAddressMacs: () => ({
        validator(_: RuleObject, value: any): Promise<void> {
            if (value) {
                const macs = value.split(";");
                for (let i = 0; i < macs.length; i++) {
                    const mac = String(macs[i]).trim();
                    if (mac && !REGEX.MAC_ADDRESS.test(mac)) {
                        return Promise.reject(new Error(t('validation.addressMac')));
                    } else {
                        for (let j = 0; j < macs.length; j++) {
                            if (i !== j && mac === macs[j].trim()) {
                                return Promise.reject(new Error(t('validation.addressMacDuplicate')));
                            }
                        }
                    }
                }
            }
            return Promise.resolve();
        },
    }),
    validatePasswordStrength: (errorMessage = t('validation.passwordStrength')) => ({
        validator(_: RuleObject, value: any): Promise<void> {
            if (!value) {
                return Promise.resolve();
            }

            // Regular expression to check for uppercase, lowercase, digit, and special character
            const hasUpperCase = /[A-Z]/.test(value);
            const hasLowerCase = /[a-z]/.test(value);
            const hasNumber = /\d/.test(value);
            const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

            if (hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar) {
                return Promise.resolve(); // Password is strong
            }

            return Promise.reject(new Error(errorMessage)); // Password does not meet the requirements
        },
    })
})
